import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import moment from "moment";
import { SpaceMessage } from "types/spaceTypes";
import { SpaceMember, SpaceGuest } from "utils/mentionUtils";
import SpaceStyleMessage from "./SpaceMessageStyle";
import SpaceDateHeader from "./SpaceMessageDateHeader";

type SpaceStyleMessageListProps = {
    messages: SpaceMessage[];
    autoScroll?: boolean;
    className?: string;
    onJumpToDate?: (option: string) => void;
    onPinMessage?: (messageId: string) => void;
    onUnpinMessage?: (messageId: string) => void;
    onEditMessage?: (messageId: string, newContent: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    onMarkAsRead?: (messageId: string) => void;
    onBatchMarkAsRead?: (messageIds: string[]) => void;
    readMessagesRef?: React.RefObject<Set<string>>;
    currentUserId?: string;
    spaceMembers?: (SpaceMember | SpaceGuest)[];
    loadMoreMessages?: () => void;
    hasMoreMessages?: boolean;
    isLoadingMore?: boolean;
};

export default function SpaceStyleMessageList({
    messages,
    autoScroll = true,
    className,
    onJumpToDate,
    onPinMessage,
    onUnpinMessage,
    onEditMessage,
    onDeleteMessage,
    onMarkAsRead,
    onBatchMarkAsRead,
    readMessagesRef,
    currentUserId,
    spaceMembers = [],
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore
}: SpaceStyleMessageListProps) {
    const listRef = useRef<HTMLDivElement>(null);


    // Infinite scroll detection for loading older messages
    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement || !loadMoreMessages) return;

        let isScrolling = false;
        
        const handleScroll = () => {
            if (isScrolling || isLoadingMore || !hasMoreMessages) {
                return;
            }

            const { scrollTop } = listElement;

            // Load more when user scrolls near the top (within 200px for better UX)
            if (scrollTop < 200) {
                isScrolling = true;
                loadMoreMessages();

                // Prevent multiple rapid calls
                setTimeout(() => {
                    isScrolling = false;
                }, 1000);
            }
        };

        listElement.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            listElement.removeEventListener('scroll', handleScroll);
        };
    }, [loadMoreMessages, hasMoreMessages, isLoadingMore]);

    // Keep track of previous message count and scroll position
    const prevMessageCountRef = useRef(messages.length);
    const prevScrollTopRef = useRef(0);
    const isLoadingOlderRef = useRef(false);
    
    // Track when we're loading older messages
    useEffect(() => {
        if (isLoadingMore) {
            isLoadingOlderRef.current = true;
            if (listRef.current) {
                prevScrollTopRef.current = listRef.current.scrollTop;
            }
        }
    }, [isLoadingMore]);
    
    useEffect(() => {
        if (!autoScroll || !listRef.current) {
            return;
        }

        const element = listRef.current;
        const { scrollTop, scrollHeight, clientHeight } = element;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
        const previousCount = prevMessageCountRef.current;
        const currentCount = messages.length;
        const wasLoadingOlder = isLoadingOlderRef.current;
        
                if (wasLoadingOlder && !isLoadingMore) {
            isLoadingOlderRef.current = false; 
            prevMessageCountRef.current = currentCount;
            return;
        }
        
        if (isLoadingMore || scrollTop < 50) {
            prevMessageCountRef.current = currentCount;
            return;
        }

        const isNewMessage = currentCount > previousCount;
        const hasScrollableContent = scrollHeight > clientHeight;
        const isGenuinelyNearBottom = hasScrollableContent && (scrollHeight - scrollTop - clientHeight < 100);
        const isNotAtTop = scrollTop > 200; 
        
        const shouldAutoScroll = isGenuinelyNearBottom && isNewMessage && isNotAtTop;
        
        if (shouldAutoScroll) {
            setTimeout(() => {
                if (element) {
                    element.scrollTop = element.scrollHeight;
                }
            }, 100);
        }
        
        // Update the previous count
        prevMessageCountRef.current = currentCount;
    }, [messages.length, autoScroll, isLoadingMore]);

    useEffect(() => {
        if (!onBatchMarkAsRead || !currentUserId || currentUserId === "unknown-user") {
            return;
        }

        const observerTargets = new Map<Element, string>(); 
        const visibleMessages = new Set<string>();
        let batchTimeout: NodeJS.Timeout | null = null;

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            let hasNewlyVisible = false;

            entries.forEach((entry) => {
                const messageId = observerTargets.get(entry.target);
                if (!messageId) return;

                if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
                    if (!visibleMessages.has(messageId)) {
                        visibleMessages.add(messageId);
                        hasNewlyVisible = true;
                    }
                } else {
                    visibleMessages.delete(messageId);
                }
            });

            if (hasNewlyVisible) {
                if (batchTimeout) {
                    clearTimeout(batchTimeout);
                }
                
                batchTimeout = setTimeout(() => {
                    const visibleMessageIds = Array.from(visibleMessages);
                    if (visibleMessageIds.length > 0) {
                        onBatchMarkAsRead(visibleMessageIds);
                    }
                }, 1000); // Add the timeout duration
            }
        };

        // Create Intersection Observer
        const observer = new IntersectionObserver(handleIntersection, {
            root: listRef.current,
            rootMargin: '0px',
            threshold: [0, 0.7, 1] // Trigger at 0%, 70%, and 100% visibility
        });

        // Observe all message elements
        const setupObserver = () => {
            if (!listRef.current) return;

            const messageElements = listRef.current.querySelectorAll('[data-message-id]');
            
            messageElements.forEach((element) => {
                const messageId = element.getAttribute('data-message-id');
                if (messageId) {
                    const messageData = messages.find(m => m.id === messageId);
                    if (messageData && messageData.author.id !== currentUserId) {
                        observerTargets.set(element, messageId);
                        observer.observe(element);
                    }
                }
            });
        };

        const setupTimeout = setTimeout(setupObserver, 100);

        return () => {
            clearTimeout(setupTimeout);
            if (batchTimeout) {
                clearTimeout(batchTimeout);
            }
            observer.disconnect();
            observerTargets.clear();
            visibleMessages.clear();
        };
    }, [messages, onBatchMarkAsRead, currentUserId]);

    const groupedMessages = React.useMemo(() => {
        const groups: {
            date: string;
            messages: (SpaceMessage & {
                showHeader: boolean;
                isGrouped: boolean;
                showDateHeader: boolean;
            })[];
        }[] = [];

        messages.forEach((message, index) => {
            const messageDate = moment(message.createdAt).format("YYYY-MM-DD");
            const prevMessage = index > 0 ? messages[index - 1] : null;

            let currentGroup = groups.find((g) => g.date === messageDate);
            if (!currentGroup) {
                currentGroup = { date: messageDate, messages: [] };
                groups.push(currentGroup);
            }

            const showHeader =
                !prevMessage ||
                prevMessage.author.id !== message.author.id ||
                new Date(message.createdAt).getTime() - new Date(prevMessage.createdAt).getTime() >
                    5 * 60 * 1000 ||
                moment(prevMessage.createdAt).format("YYYY-MM-DD") !== messageDate; // New day = new header

            const isGrouped =
                prevMessage &&
                prevMessage.author.id === message.author.id &&
                moment(prevMessage.createdAt).format("YYYY-MM-DD") === messageDate &&
                new Date(message.createdAt).getTime() - new Date(prevMessage.createdAt).getTime() <=
                    5 * 60 * 1000;

            const showDateHeader = currentGroup.messages.length === 0;

            currentGroup.messages.push({
                ...message,
                showHeader,
                isGrouped: isGrouped && !showHeader,
                showDateHeader,
            });
        });

        const sortedGroups = groups.sort((a, b) => {
            return moment(a.date).valueOf() - moment(b.date).valueOf();
        });


        return sortedGroups;
    }, [messages]);

    return (
        <div className="bg-white h-full">
            <div
                ref={listRef}
                className={clsx(
                    "h-full overflow-y-auto overflow-x-hidden bg-white px-4 py-2 scrollbar-hidden min-w-0",
                    className
                )}
            >
                {hasMoreMessages && !isLoadingMore && (
                    <div className="flex justify-center py-4">
                           <button
                               onClick={() => loadMoreMessages?.()}
                               className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                           >
                               Load older messages
                           </button>
                    </div>
                )}

                {isLoadingMore && (
                    <div className="flex justify-center py-4">
                        <div className="flex items-center space-x-2 text-gray-500">
                            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                            <span className="text-sm">Loading older messages...</span>
                        </div>
                    </div>
                )}

                {groupedMessages.map((group, groupIndex) => {
                    const isOnlyWelcomeMessage = messages.length === 1 && messages[0]?.messageType === "welcome";
                    
                    return (
                    <React.Fragment key={group.date}>
                        {!isOnlyWelcomeMessage && (
                        <SpaceDateHeader
                            date={group.date}
                            onJumpToDate={onJumpToDate}
                            className={clsx(
                                    "sticky top-0 z-10"
                            )}
                        />
                        )}

                        {group.messages.map((message) => (
                            <SpaceStyleMessage
                                key={message.id}
                                message={message}
                                showHeader={message.showHeader}
                                isGrouped={message.isGrouped}
                                onPinMessage={onPinMessage}
                                onUnpinMessage={onUnpinMessage}
                                onEditMessage={onEditMessage}
                                onDeleteMessage={onDeleteMessage}
                                onMarkAsRead={onMarkAsRead}
                                readMessagesRef={readMessagesRef}
                                currentUserId={currentUserId}
                                spaceMembers={spaceMembers}
                            />
                        ))}
                    </React.Fragment>
                    );
                })}

                <div className="h-4"></div>
            </div>
        </div>
    );
}
